import React, { useCallback, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import { SanityBlockBuilderTestimonialCarousel } from '../../../models/gatsby-graphql-types';
import PageWrapper from '../../page-wrapper';
import sharedStyles from '../../styles';
import breakpoints from '../../breakpoints';
import CarouselTestimonial from '../../CarouselTestimonial';

const InnerContainer = styled.div`
  display: flex;
  max-width: ${breakpoints.xl};
  margin-block: ${sharedStyles.spacing['spacing-2xl']};
  flex-direction: column;
`;

const Headline = styled.h2`
  margin: 0 auto ${sharedStyles.spacing['spacing-md']} auto;
  text-align: center;
`;

const TestimonialGroup = styled.ul`
  display: flex;
  flex-wrap: wrap;
  overflow-x: auto;
  gap: ${sharedStyles.spacing['spacing-lg']};
  margin: 0;
  padding: 0;

  @media screen and (min-width: ${breakpoints.sm}) {
    flex-wrap: nowrap;
    overflow: hidden;

    > li {
      width: calc(33.33% - ${sharedStyles.spacing['spacing-lg']});
    }
  }
`;

const TestimonialWrapper = motion(styled.li`
  min-width: 100%;
  display: flex;
  min-height: 500px;
  @media screen and (min-width: ${breakpoints.sm}) {
    min-width: calc(33.33% - ${sharedStyles.spacing['spacing-lg']});
  }
`);

const Navigation = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: ${sharedStyles.spacing['spacing-md']};
  margin-bottom: ${sharedStyles.spacing['spacing-md']};
  gap: ${sharedStyles.spacing['spacing-md']};
`;

const NavButton = styled.button`
  border: none;
  cursor: pointer;
  font-size: ${sharedStyles.fontSize['2xl']};
  display: flex;
  align-items: center;
  border-radius: 4px;
  transition: all 0.3s ease-in-out;
  color: ${({ theme }) => theme.colors.brandPrimary};
  max-width: 40px;
  padding-top: ${sharedStyles.spacing['spacing-1xs']};
  padding-right: ${sharedStyles.spacing['spacing-sm']};
  padding-bottom: ${sharedStyles.spacing['spacing-1xs']};
  padding-left: ${sharedStyles.spacing['spacing-sm']};

  &:hover {
    cursor: pointer !important;
    transform: scale(1.05);
    transition: all 0.3s ease-in-out;
  }
`;

const Label = styled.span`
  font-size: ${sharedStyles.fontSize.lg};
  color: ${({ theme }) => theme.colors.text};
`;

export type BlockBuilderTestimonialCarouselProps = {
  value: SanityBlockBuilderTestimonialCarousel;
};

function BlockBuilderTestimonialCarousel({
  value,
}: Readonly<BlockBuilderTestimonialCarouselProps>) {
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(3); // Default to 3, will be updated in useEffect if window is available
  const containerRef = useRef<HTMLUListElement>(null);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const { tt } = useUiComponentsInternationalContext();

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      setItemsPerPage(window.innerWidth < parseInt(breakpoints.sm, 10) ? 1 : 3);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const nextPage = () => {
    setCurrentPage(
      prevPage => (prevPage + 1) % Math.ceil(value.items.length / itemsPerPage)
    );
  };

  const prevPage = () => {
    setCurrentPage(
      previousPage =>
        (previousPage - 1 + Math.ceil(value.items.length / itemsPerPage)) %
        Math.ceil(value.items.length / itemsPerPage)
    );
  };

  const mobileNextPage = useCallback(() => {
    setCurrentPage(
      previousPage => (previousPage + 1) % Math.ceil(value.items.length / 1)
    );
  }, [value.items.length]);

  const mobilePrevPage = useCallback(() => {
    setCurrentPage(
      previousPage =>
        (previousPage - 1 + Math.ceil(value.items.length / 1)) %
        Math.ceil(value.items.length / 1)
    );
  }, [value.items.length]);

  const handleSwipe = useCallback(
    e => {
      const touchStart = e.touches[0].clientX;

      const handleTouchMove = evt => {
        const touchEnd = evt.touches[0].clientX;
        if (touchStart - touchEnd > 50) {
          mobileNextPage();
          containerRef.current.removeEventListener(
            'touchmove',
            handleTouchMove
          );
        } else if (touchEnd - touchStart > 50) {
          mobilePrevPage();
          containerRef.current.removeEventListener(
            'touchmove',
            handleTouchMove
          );
        }
      };

      containerRef.current.addEventListener('touchmove', handleTouchMove);
    },
    [mobileNextPage, mobilePrevPage]
  );

  useEffect(() => {
    const container = containerRef.current;
    container.addEventListener('touchstart', handleSwipe);

    return () => {
      container.removeEventListener('touchstart', handleSwipe);
    };
  }, [handleSwipe]);

  useEffect(() => {
    setIsInitialRender(false);
  }, []);

  const displayedItems = value.items.slice(
    currentPage * itemsPerPage,
    currentPage * itemsPerPage + itemsPerPage
  );

  return (
    <PageWrapper fullWidth background="transparent" padding="0">
      <InnerContainer role="region" aria-label="Carousel">
        {value?.headline && <Headline>{value?.headline}</Headline>}
        {value?.items?.length > itemsPerPage && (
          <Navigation>
            <NavButton
              onClick={prevPage}
              aria-label={tt(
                s =>
                  s.blockBuilderTestimonialCarouselComponent
                    .previousTestimonialsAriaLabel
              )}
              aria-controls="testimonial-group"
            >
              <MdChevronLeft />
            </NavButton>
            <Label>
              {currentPage + 1} / {Math.ceil(value.items.length / itemsPerPage)}
            </Label>
            <NavButton
              onClick={nextPage}
              aria-label={tt(
                s =>
                  s.blockBuilderTestimonialCarouselComponent
                    .nextTestimonialsAriaLabel
              )}
              aria-controls="testimonial-group"
            >
              <MdChevronRight />
            </NavButton>
          </Navigation>
        )}
        <TestimonialGroup
          ref={containerRef}
          aria-live="polite"
          id="testimonial-group"
        >
          <AnimatePresence mode="wait">
            {displayedItems.map(item => (
              <TestimonialWrapper
                key={item._key}
                initial={isInitialRender ? false : { opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.3 }}
                tabIndex={0}
              >
                <CarouselTestimonial {...item} />
              </TestimonialWrapper>
            ))}
          </AnimatePresence>
        </TestimonialGroup>
      </InnerContainer>
    </PageWrapper>
  );
}

export default BlockBuilderTestimonialCarousel;
